/**
 * AdminValidaciones · /admin/validaciones · v101.102.0 · Centro de Validaciones
 *  · Qué valida el sistema en cada momento (aprovisionar / finalizar / cerrar) y para qué flujo.
 *  · Prender / apagar cada validación (con motivo; queda en la bitácora).
 *  · Reglas nuevas sin programar: campo de la OT + condición + mensaje, por momento, flujo, actividad y segmento.
 *  · Probar con una OT: qué le aplica y si cumple las reglas nuevas (no cambia nada).
 * API: /v1/validaciones (validaciones.py).
 */
import { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api, { extractApiError } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Plus, Loader2, Save, X, Edit3, Search, History, AlertTriangle, CheckCircle2, XCircle, BookOpen, Info, ChevronLeft, ChevronRight } from "lucide-react";
import LogicaAprovisionamiento from "../components/LogicaAprovisionamiento";   // v101.129.0

// v101.110.0 · «devolver» solo tiene validaciones del sistema; las reglas nuevas se crean en los 3 primeros momentos
const STAGE_ORDER = ["agendar", "aprovisionar", "finalizar", "cerrar", "devolver", "cancelar"];   // v101.252.0 · + agendar   // v101.134.0 · + cancelar
const RULE_STAGES = ["aprovisionar", "finalizar", "cerrar"];
const EMPTY_RULE = { name: "", stage: "aprovisionar", field: "", operator: "requerido", value: "", message: "",
  flows: [], activity_codes: [], segments: [], enabled: true };

function Switch({ on, disabled, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid} title={on ? "Prendida" : "Apagada"}
      className={`relative w-11 h-6 rounded-full transition ${on ? "bg-emerald-500" : "bg-slate-300"} disabled:opacity-50`}>
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}

function FlowChips({ flows, names }) {
  if (!flows || flows.length === 0) return <span className="text-[10px] text-slate-500">Todos</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {flows.map((f) => (
        <span key={f} className="px-1.5 py-0.5 rounded bg-slate-100 text-[10px] text-slate-700">{names?.[f] || `Flujo ${f}`}</span>
      ))}
    </div>
  );
}

export default function AdminValidaciones() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flowFilter, setFlowFilter] = useState("");
  const [toggle, setToggle] = useState(null);     // {kind:'system'|'rule', item, enabled}
  const [infoFor, setInfoFor] = useState(null);   // v101.135.0 · asistente informativo de la validación
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);         // regla en edición
  const [catalog, setCatalog] = useState({ activities: [], segments: [] });
  const [changes, setChanges] = useState(null);
  const [probarQ, setProbarQ] = useState("");
  const [probar, setProbar] = useState(null);
  const [probando, setProbando] = useState(false);
  // v101.129.0 · Pestañas: validaciones (prender/apagar) · lógica de aprovisionamiento (documento)
  const [tab, setTab] = useState(() => (window.location.hash === "#logica" ? "logica" : "validaciones"));
  const goTab = (t) => { setTab(t); window.history.replaceState(null, "", t === "logica" ? "#logica" : window.location.pathname); };

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/v1/validaciones");
      setData(d);
    } catch (e) {
      toast.error(extractApiError(e, "No se pudo cargar el Centro de Validaciones"));
    } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    api.get("/v1/validaciones/activity-types").then(({ data: d }) => setCatalog(d || { activities: [], segments: [] })).catch(() => {});
  }, []);

  const flows = data?.flows || {};
  const canEdit = !!data?.can_edit;
  const matchFlow = (list) => !flowFilter || !list || list.length === 0 || list.map(String).includes(String(flowFilter));
  const byStage = useMemo(() => {
    const out = {};
    for (const st of STAGE_ORDER) {
      out[st] = {
        system: (data?.system || []).filter((b) => b.stage === st && matchFlow(b.flows)),
        rules: (data?.rules || []).filter((r) => r.stage === st && matchFlow(r.flows)),
      };
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, flowFilter]);

  const confirmToggle = async () => {
    if (note.trim().length < 5) { toast.error("Escriba el motivo del cambio (mínimo 5 caracteres)"); return; }
    setBusy(true);
    try {
      if (toggle.kind === "system") {
        await api.put(`/v1/validaciones/system/${toggle.item.code}`, { enabled: toggle.enabled, note });
      } else {
        const r = toggle.item;
        await api.put(`/v1/validaciones/rules/${r.id}`, { ...r, enabled: toggle.enabled, note });
      }
      toast.success(toggle.enabled ? "Validación prendida" : "Validación apagada");
      setToggle(null); setNote(""); setChanges(null);
      load();
    } catch (e) {
      toast.error(extractApiError(e, "No se pudo cambiar"));
    } finally { setBusy(false); }
  };

  const saveRule = async () => {
    setBusy(true);
    try {
      if (edit.id) await api.put(`/v1/validaciones/rules/${edit.id}`, edit);
      else await api.post("/v1/validaciones/rules", edit);
      toast.success("Regla guardada");
      setEdit(null); setChanges(null);
      load();
    } catch (e) {
      toast.error(extractApiError(e, "No se pudo guardar la regla"));
    } finally { setBusy(false); }
  };

  const runProbar = async () => {
    if (!probarQ.trim()) return;
    setProbando(true); setProbar(null);
    try {
      const { data: d } = await api.get("/v1/validaciones/probar", { params: { ot: probarQ.trim() } });
      setProbar(d);
    } catch (e) {
      toast.error(extractApiError(e, "No se pudo probar la OT"));
    } finally { setProbando(false); }
  };

  const loadChanges = async () => {
    try {
      const { data: d } = await api.get("/v1/validaciones/changes");
      setChanges(d?.items || []);
    } catch (e) { toast.error(extractApiError(e, "No se pudo cargar la bitácora")); }
  };

  const toggleList = (key, val) => setEdit((cur) => {
    const list = cur[key] || [];
    return { ...cur, [key]: list.includes(val) ? list.filter((x) => x !== val) : [...list, val] };
  });
  const needsValue = edit && !["requerido", "vacio"].includes(edit.operator);

  return (
    <Layout>
      <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ShieldCheck size={22} className="text-slate-700" />
            <div>
              <h1 className="text-lg font-bold text-slate-900">Centro de Validaciones</h1>
              <p className="text-xs text-slate-500">Qué se exige al aprovisionar, al finalizar y al cerrar cada OT. Prenda o apague
                validaciones y cree reglas nuevas. Todo cambio queda en la bitácora.</p>
            </div>
          </div>
          {canEdit && (
            <button onClick={() => setEdit({ ...EMPTY_RULE })} data-testid="btn-new-rule"
              className="px-3 py-2 rounded-xl text-xs font-bold text-white bg-slate-800 hover:bg-slate-900 flex items-center gap-1.5">
              <Plus size={14} /> Nueva regla
            </button>
          )}
        </div>

        {/* v101.129.0 · Pestañas */}
        <div className="flex gap-1 border-b border-slate-200" data-testid="validaciones-tabs">
          {[["validaciones", "Validaciones", ShieldCheck], ["logica", "Lógica de aprovisionamiento", BookOpen]].map(([k, label, Icon]) => (
            <button key={k} onClick={() => goTab(k)} data-testid={`tab-${k}`}
              className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 -mb-px ${tab === k ? "border-[#0A3747] text-[#0A3747]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {tab === "logica" && <LogicaAprovisionamiento system={data?.system || []} />}

        {tab === "validaciones" && (<>
        {/* Filtro por flujo + probar */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-3">
            <div className="text-[11px] font-bold uppercase text-slate-500 mb-2">Ver por tipo de aprovisionamiento</div>
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => setFlowFilter("")}
                className={`px-2.5 py-1 rounded-lg text-xs border ${!flowFilter ? "bg-slate-800 text-white border-slate-800" : "border-slate-300 text-slate-700"}`}>Todos</button>
              {Object.entries(flows).map(([k, v]) => (
                <button key={k} onClick={() => setFlowFilter(k)} data-testid={`flow-filter-${k}`}
                  className={`px-2.5 py-1 rounded-lg text-xs border ${String(flowFilter) === k ? "bg-slate-800 text-white border-slate-800" : "border-slate-300 text-slate-700"}`}>{v}</button>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-3">
            <div className="text-[11px] font-bold uppercase text-slate-500 mb-2">Probar con una OT (no cambia nada)</div>
            <div className="flex gap-2">
              <input value={probarQ} onChange={(e) => setProbarQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runProbar()}
                placeholder="Número de OT (ej. 00332681)" className="flex-1 px-3 py-2 rounded-xl border border-slate-300 text-sm"
                data-testid="input-probar-ot" />
              <button onClick={runProbar} disabled={probando}
                className="px-3 py-2 rounded-xl text-xs font-bold text-white bg-sky-700 hover:bg-sky-800 disabled:opacity-40 flex items-center gap-1.5">
                {probando ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} Probar
              </button>
            </div>
          </div>
        </div>

        {probar && (
          <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4 space-y-2" data-testid="probar-result">
            <div className="flex items-center justify-between">
              <div className="text-sm font-bold text-sky-900">
                OT {probar.task_number || probar.odoo_task_id} · {probar.flujo_name}
                <span className="ml-2 text-[11px] font-normal text-sky-800">{probar.activity_type_code || ""} · {probar.segment_name || ""}</span>
              </div>
              <button onClick={() => setProbar(null)} className="text-sky-700"><X size={16} /></button>
            </div>
            {STAGE_ORDER.map((st) => (
              <div key={st} className="text-xs">
                <div className="font-bold text-slate-700">{data?.stages?.[st]}</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(probar.system || []).filter((b) => b.stage === st).map((b) => (
                    <span key={b.code} className={`px-1.5 py-0.5 rounded text-[10px] ${b.enabled ? "bg-white text-slate-700 border border-slate-200" : "bg-slate-200 text-slate-500 line-through"}`}>{b.name}</span>
                  ))}
                </div>
                {(probar.rules?.[st] || []).map((r) => (
                  <div key={r.id} className="flex items-center gap-1.5 mt-1">
                    {r.ok ? <CheckCircle2 size={13} className="text-emerald-600" /> : <XCircle size={13} className="text-rose-600" />}
                    <span className="font-semibold">{r.name}</span>
                    <span className="text-slate-500">({data?.fields?.[r.field] || r.field}: {r.value === null || r.value === undefined || r.value === "" ? "vacío" : String(r.value)})</span>
                    {!r.ok && <span className="text-rose-700">→ {r.message}</span>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Cargando…</div>
        ) : STAGE_ORDER.map((st) => (
          <div key={st} className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`stage-${st}`}>
            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-sm font-bold text-slate-800">{data?.stages?.[st]}</div>
            <div className="divide-y divide-slate-100">
              {byStage[st].system.map((b) => (
                <div key={b.code} className={`px-4 py-3 flex items-start gap-3 ${b.enabled ? "" : "bg-slate-50"}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900">{b.name}</span>
                      <button type="button" onClick={() => setInfoFor(b)} title="Cómo funciona esta validación"
                        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-sky-50 text-sky-700 hover:bg-sky-100"
                        data-testid={`info-${b.code}`}><Info size={12} /></button>
                      <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[10px] font-bold">Sistema</span>
                      {b.critical && <span className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 text-[10px] font-bold">Crítica</span>}
                      {!b.enabled && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold">APAGADA</span>}
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">{b.desc}</div>
                    <div className="mt-1"><FlowChips flows={b.flows} names={flows} /></div>
                    {b.updated_by && <div className="text-[10px] text-slate-400 mt-1">Último cambio: {b.updated_by} · {b.updated_at ? new Date(b.updated_at).toLocaleString() : ""}{b.note ? ` · «${b.note}»` : ""}</div>}
                  </div>
                  <Switch on={b.enabled} disabled={!canEdit} testid={`switch-${b.code}`}
                    onClick={() => { setToggle({ kind: "system", item: b, enabled: !b.enabled }); setNote(""); }} />
                </div>
              ))}
              {byStage[st].rules.map((r) => (
                <div key={r.id} className={`px-4 py-3 flex items-start gap-3 ${r.enabled ? "" : "bg-slate-50"}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900">{r.name}</span>
                      <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-bold">Regla nueva</span>
                      {!r.enabled && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold">APAGADA</span>}
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      {data?.fields?.[r.field] || r.field} {data?.operators?.[r.operator] || r.operator}{r.value ? ` «${r.value}»` : ""} · si no: «{r.message}»
                    </div>
                    <div className="mt-1 flex flex-wrap gap-2 items-center">
                      <FlowChips flows={r.flows} names={flows} />
                      {(r.activity_codes || []).length > 0 && <span className="text-[10px] text-slate-500">Actividades: {r.activity_codes.join(", ")}</span>}
                      {(r.segments || []).length > 0 && <span className="text-[10px] text-slate-500">Segmentos: {r.segments.join(", ")}</span>}
                    </div>
                  </div>
                  {canEdit && (
                    <button onClick={() => setEdit({ ...r })} className="text-xs font-bold text-slate-600 hover:text-slate-900 flex items-center gap-1 mt-0.5">
                      <Edit3 size={12} /> Editar
                    </button>
                  )}
                  <Switch on={r.enabled} disabled={!canEdit} testid={`switch-rule-${r.id}`}
                    onClick={() => { setToggle({ kind: "rule", item: r, enabled: !r.enabled }); setNote(""); }} />
                </div>
              ))}
              {byStage[st].system.length + byStage[st].rules.length === 0 && (
                <div className="px-4 py-3 text-xs text-slate-500">Nada para este filtro.</div>
              )}
            </div>
          </div>
        ))}

        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-800"><History size={15} /> Bitácora de cambios</div>
            {changes === null && <button onClick={loadChanges} className="text-xs font-bold text-sky-700">Ver</button>}
          </div>
          {changes !== null && (
            <div className="mt-2 max-h-72 overflow-y-auto divide-y divide-slate-100">
              {changes.length === 0 ? <div className="text-xs text-slate-500 py-2">Sin cambios todavía.</div> : changes.map((c, i) => (
                <div key={i} className="py-1.5 text-xs">
                  <span className="font-semibold">{c.changed_by}</span> · {c.action} · <span className="text-slate-700">{c.target_name}</span>
                  {c.detail && <span className="text-slate-500"> — {c.detail}</span>}
                  <span className="text-slate-400"> · {c.changed_at ? new Date(c.changed_at).toLocaleString() : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        </>)}
      </div>

      {/* Confirmar prender / apagar */}
      {infoFor && <ValidationInfoWizard item={infoFor} flows={flows} stageName={data?.stages?.[infoFor.stage]}
        onClose={() => setInfoFor(null)} />}
      {toggle && (
        <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4" onClick={() => setToggle(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="toggle-dialog">
            <div className="px-5 py-4 border-b border-slate-200 text-sm font-bold text-slate-900">
              {toggle.enabled ? "Prender" : "Apagar"} · {toggle.item.name}
            </div>
            <div className="px-5 py-4 space-y-3">
              {!toggle.enabled && toggle.kind === "system" && (
                <div className={`p-3 rounded-xl text-xs flex gap-2 ${toggle.item.critical ? "bg-rose-50 text-rose-800 border border-rose-200" : "bg-amber-50 text-amber-800 border border-amber-200"}`}>
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <div><b>Si la apaga:</b> {toggle.item.off}{toggle.item.critical ? " Es una validación crítica." : ""}</div>
                </div>
              )}
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-600">Motivo del cambio</label>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={500}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 text-sm" placeholder="Por qué se cambia (queda en la bitácora)" />
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setToggle(null)} className="px-3 py-2 rounded-xl text-xs font-bold border border-slate-300">Cancelar</button>
              <button onClick={confirmToggle} disabled={busy}
                className={`px-4 py-2 rounded-xl text-xs font-bold text-white disabled:opacity-40 flex items-center gap-1.5 ${toggle.enabled ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}>
                {busy && <Loader2 size={13} className="animate-spin" />} {toggle.enabled ? "Prender" : "Apagar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Editor de regla */}
      {edit && (
        <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4" onClick={() => setEdit(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}
               data-testid="rule-editor">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="text-sm font-bold text-slate-900">{edit.id ? "Editar regla" : "Nueva regla"}</div>
              <button onClick={() => setEdit(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="px-5 py-4 space-y-3 overflow-y-auto">
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-600">Nombre</label>
                <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} maxLength={160}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 text-sm" placeholder="Ej.: Coordenadas del cliente obligatorias" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-[11px] font-bold uppercase text-slate-600">Momento</label>
                  <select value={edit.stage} onChange={(e) => setEdit({ ...edit, stage: e.target.value })}
                    className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 text-sm">
                    {RULE_STAGES.map((st) => <option key={st} value={st}>{data?.stages?.[st]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase text-slate-600">Campo de la OT</label>
                  <select value={edit.field} onChange={(e) => setEdit({ ...edit, field: e.target.value })}
                    className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 text-sm">
                    <option value="">Elegir…</option>
                    {Object.entries(data?.fields || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase text-slate-600">Debe cumplir</label>
                  <select value={edit.operator} onChange={(e) => setEdit({ ...edit, operator: e.target.value })}
                    className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 text-sm">
                    {Object.entries(data?.operators || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
              {needsValue && (
                <div>
                  <label className="text-[11px] font-bold uppercase text-slate-600">Valor</label>
                  <input value={edit.value || ""} onChange={(e) => setEdit({ ...edit, value: e.target.value })}
                    className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 text-sm font-mono" />
                </div>
              )}
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-600">Mensaje si NO se cumple (lo ve el técnico / operador)</label>
                <input value={edit.message} onChange={(e) => setEdit({ ...edit, message: e.target.value })} maxLength={500}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 text-sm" placeholder="Ej.: Actualice el GPS del cliente antes de aprovisionar." />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-600">Tipos de aprovisionamiento (ninguno = todos)</label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {Object.entries(flows).map(([k, v]) => {
                    const on = (edit.flows || []).map(String).includes(k);
                    return (
                      <button key={k} type="button" onClick={() => toggleList("flows", Number(k))}
                        className={`px-2.5 py-1 rounded-lg text-xs border ${on ? "bg-slate-800 text-white border-slate-800" : "border-slate-300 text-slate-700"}`}>{v}</button>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold uppercase text-slate-600">Actividades (ninguna = todas)</label>
                  <div className="mt-1 max-h-40 overflow-y-auto rounded-xl border border-slate-200 divide-y divide-slate-100">
                    {(catalog.activities || []).map((a) => (
                      <label key={a.code} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                        <input type="checkbox" checked={(edit.activity_codes || []).includes(a.code)} onChange={() => toggleList("activity_codes", a.code)} />
                        <span>{a.name || a.code}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase text-slate-600">Segmentos (ninguno = todos)</label>
                  <div className="mt-1 max-h-40 overflow-y-auto rounded-xl border border-slate-200 divide-y divide-slate-100">
                    {(catalog.segments || []).map((sg) => (
                      <label key={sg} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                        <input type="checkbox" checked={(edit.segments || []).includes(sg)} onChange={() => toggleList("segments", sg)} />
                        <span>{sg}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!edit.enabled} onChange={(e) => setEdit({ ...edit, enabled: e.target.checked })} /> Prendida
              </label>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setEdit(null)} className="px-3 py-2 rounded-xl text-xs font-bold border border-slate-300">Cancelar</button>
              <button onClick={saveRule} disabled={busy}
                className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1.5">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Guardar regla
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}


/* ── v101.135.0 · Asistente informativo de una validación del sistema ──
   5 pasos: cómo funciona · qué valida · campos que verifica · por qué existe la regla · mensajes al usuario. */
function ValidationInfoWizard({ item, flows, stageName, onClose }) {
  const inf = item?.info || {};
  const steps = [
    { key: "como", title: "Cómo funciona", body: <p className="text-sm text-slate-700 leading-relaxed">{inf.como || item.desc}</p> },
    { key: "valida", title: "Qué valida", body: <p className="text-sm text-slate-700 leading-relaxed">{inf.valida || item.desc}</p> },
    { key: "campos", title: "Campos que verifica", body: (
      <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
        {(inf.campos || []).map((c) => <li key={c} className="font-mono text-[12px]">{c}</li>)}
        {!(inf.campos || []).length && <li>Sin campos específicos.</li>}
      </ul>) },
    { key: "porque", title: "Por qué existe la regla", body: (
      <div className="space-y-2 text-sm text-slate-700">
        <p className="leading-relaxed">{inf.porque || "—"}</p>
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-[12px] text-amber-900">
          <b>Si se apaga:</b> {item.off || "—"}
        </div>
      </div>) },
    { key: "mensajes", title: "Mensajes al usuario cuando falla", body: (
      <ul className="space-y-1.5">
        {(inf.mensajes || []).map((m, i) => (
          <li key={i} className="rounded-lg bg-rose-50 border border-rose-200 px-2.5 py-1.5 text-[12px] text-rose-900">{m}</li>
        ))}
        {!(inf.mensajes || []).length && <li className="text-sm text-slate-500">Sin mensaje (no bloquea).</li>}
      </ul>) },
  ];
  const [i, setI] = useState(0);
  const st = steps[i];
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="validation-info-wizard">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] text-slate-500">{stageName} · <span className="font-mono">{item.code}</span></div>
            <div className="text-base font-bold text-slate-900">{item.name}</div>
            <div className="mt-1 flex flex-wrap gap-1">
              {(item.flows || []).map((f) => (
                <span key={f} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px]">{flows?.[f] || `Flujo ${f}`}</span>
              ))}
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${item.enabled ? "bg-emerald-50 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
                {item.enabled ? "PRENDIDA" : "APAGADA"}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={18} /></button>
        </div>
        <div className="px-5 pt-3 flex gap-1">
          {steps.map((s_, k) => (
            <button key={s_.key} onClick={() => setI(k)} title={s_.title}
              className={`flex-1 h-1.5 rounded-full ${k <= i ? "bg-sky-600" : "bg-slate-200"}`} data-testid={`info-step-${s_.key}`} />
          ))}
        </div>
        <div className="px-5 py-4 min-h-[180px]">
          <div className="text-[11px] font-bold uppercase tracking-wider text-sky-700 mb-2">Paso {i + 1} de {steps.length} · {st.title}</div>
          {st.body}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-between">
          <button onClick={() => setI(Math.max(0, i - 1))} disabled={i === 0}
            className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-semibold text-slate-700 disabled:opacity-40 flex items-center gap-1">
            <ChevronLeft size={14} /> Anterior
          </button>
          {i < steps.length - 1 ? (
            <button onClick={() => setI(i + 1)} className="px-3 py-1.5 rounded-lg bg-sky-700 text-white text-xs font-semibold flex items-center gap-1" data-testid="info-next">
              Siguiente <ChevronRight size={14} />
            </button>
          ) : (
            <button onClick={onClose} className="px-3 py-1.5 rounded-lg bg-slate-800 text-white text-xs font-semibold">Entendido</button>
          )}
        </div>
      </div>
    </div>
  );
}
